/**
 * Centralized date formatting for Trackeate (locale es-AR).
 *
 * The whole app routes through these helpers so we never re-introduce
 * US-style formats like "06-09" or "Jun 9" that confuse Argentinian users.
 *
 * Inputs are always ISO dates in `YYYY-MM-DD` (or full ISO timestamps with
 * `T...` suffix). They are parsed in *local* time to avoid timezone drift.
 *
 * Outputs follow Argentinian conventions:
 *   formatDateAR("2026-06-09")        → "9 de junio de 2026"
 *   formatShortDateAR("2026-06-09")   → "9 Jun"
 *   formatAxisDateAR("2026-06-09")    → "9 Jun"      (chart axis ticks)
 *   formatTooltipDateAR("2026-06-09") → "9 de junio de 2026"
 *   formatNumericDateAR("2026-06-09") → "09/06/2026"
 */

const MONTHS_SHORT_AR = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const MONTHS_FULL_AR = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function _parseLocal(iso) {
  if (!iso) return null;
  // Accept "YYYY-MM-DD" and full ISO timestamps ("YYYY-MM-DDThh:mm:ss...")
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split("-").map((v) => parseInt(v, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** "9 de junio de 2026" — long form, used in tooltips and headers. */
export function formatDateAR(iso) {
  const d = _parseLocal(iso);
  if (!d) return "";
  return `${d.getDate()} de ${MONTHS_FULL_AR[d.getMonth()]} de ${d.getFullYear()}`;
}

/** "9 Jun" — short form for chart axis ticks and compact lists. */
export function formatShortDateAR(iso) {
  const d = _parseLocal(iso);
  if (!d) return "";
  return `${d.getDate()} ${MONTHS_SHORT_AR[d.getMonth()]}`;
}

/** Alias for axis usage — keeps intent explicit at call site. */
export const formatAxisDateAR = formatShortDateAR;

/** Alias for tooltip usage — keeps intent explicit at call site. */
export const formatTooltipDateAR = formatDateAR;

/** "09/06/2026" — DD/MM/YYYY, when a strictly numeric form is required. */
export function formatNumericDateAR(iso) {
  const d = _parseLocal(iso);
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}
